import dayjs from "dayjs";
import styled from "styled-components";

import { formatDate } from "@similarweb/pro-logic-components";
import { setFont, colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { PoPCompareChangeTooltip } from "pro-features/components/PoPCompareChangeTooltip/PoPCompareChangeTooltip";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import ChartTooltipAlert from "app/pages/app-intelligence/app-analysis/pages/Engagement/components/ChartTooltipAlert/ChartTooltipAlert";
import { getGA4AlertText } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/CommonChartTooltip";
import { BoldText, GridBox, Line } from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { WEB_SOURCE_DISPLAY_NAME } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TooltipPartialText } from "./TooltipPartialText";

export const PopTooltip = (props: any) => (
    <PoPCompareChangeTooltip
        headersList={[
            "Domain",
            formatDate(props.pointsData[0].point.values[1].Key),
            formatDate(props.pointsData[0].point.values[0].Key),
            "Growth",
        ]}
        {...props}
    />
);

const Text = styled.label<{ color?: string }>`
    cursor: inherit;
    ${setFont({ $size: 13 })}
    color: ${colorsPalettes.carbon[200]};
    ${({ color }) => color && `color:${color};`};
`;

const PopTooltipSingleDefault = (props) => {
    const { pointsData, yFormatter, date, compareToDate, domainsToText } = props;
    const { webSource } = proNavigator.getParams();
    const isMTDPartialData = pointsData[0]?.point?.isPartial;
    return (
        <>
            <GridBox>
                <span />
                <BoldText color={pointsData[1].color}>{date}</BoldText>
                <BoldText color={pointsData[0].color}>
                    {compareToDate}
                    {isMTDPartialData ? "*" : null}
                </BoldText>
                <BoldText color={colorsPalettes.carbon[500]}>Change</BoldText>
                <Line />
            </GridBox>
            <PopTooltipSingleDefaultRow
                yFormatter={yFormatter}
                pointZero={pointsData[0].y}
                pointOne={pointsData[1].y}
                webSource={webSource}
            />
            {isMTDPartialData ? (
                <TooltipPartialText>
                    {translate("wa.traffic.engagement.over.time.single.pop.partial", {
                        last_supported_day: dayjs(swSettings.current.lastSupportedDailyDate).format("MMM DD"),
                        month: compareToDate,
                    })}
                </TooltipPartialText>
            ) : null}
            {domainsToText ? (
                <ChartTooltipAlert
                    text={translate("website.analysis.traffic.and.engagement.overtime.tooltip.ga4.alert", {
                        domains: domainsToText,
                    })}
                />
            ) : null}
        </>
    );
};
const PopTooltipSingleDefaultRow = ({ yFormatter, webSource, pointZero, pointOne }) => {
    const dateValue = yFormatter({ value: pointOne });
    const compareToDateValue = yFormatter({ value: pointZero });
    const change = pointZero / pointOne - 1;
    const changeColor = change > 0 ? colorsPalettes.green.s100 : colorsPalettes.red.s100;
    return (
        <GridBox>
            <Text>{WEB_SOURCE_DISPLAY_NAME[webSource] ? WEB_SOURCE_DISPLAY_NAME[webSource] : webSource}</Text>
            <Text>{dateValue}</Text>
            <Text>{compareToDateValue}</Text>
            <Text color={changeColor}>{percentageSignFilter()(change, 2)}</Text>
        </GridBox>
    );
};
const PopTooltipSingleCombined = (props) => {
    const { pointsData, yFormatter, date, compareToDate } = props;
    return (
        <>
            <GridBox>
                <span />
                <BoldText color={pointsData[1].color}>{date}</BoldText>
                <BoldText color={pointsData[0].color}>{compareToDate}</BoldText>
                <BoldText color={colorsPalettes.carbon[500]}>Change</BoldText>
                <Line />
            </GridBox>
            <PopTooltipSingleDefaultRow
                yFormatter={yFormatter}
                pointZero={pointsData[0].y}
                pointOne={pointsData[1].y}
                webSource={WEB_SOURCE_DISPLAY_NAME.Desktop}
            />
            <PopTooltipSingleDefaultRow
                yFormatter={yFormatter}
                pointZero={pointsData[2].y}
                pointOne={pointsData[3].y}
                webSource={WEB_SOURCE_DISPLAY_NAME.MobileWeb}
            />
            <PopTooltipSingleDefaultRow
                yFormatter={yFormatter}
                pointZero={pointsData[0].y + pointsData[2].y}
                pointOne={pointsData[1].y + pointsData[3].y}
                webSource={WEB_SOURCE_DISPLAY_NAME.Total}
            />
        </>
    );
};

// two point for default state and four points for combined state
export const PopTooltipSingle = (props) => {
    const date = formatDate(props.pointsData[0].point.values[1].Key);
    const compareToDate = formatDate(props.pointsData[0].point.values[0].Key);
    const domainsToText = getGA4AlertText(props.GA4FirstDataPointDate, props.x);
    if (props.pointsData.length === 2) {
        return (
            <PopTooltipSingleDefault
                {...props}
                date={date}
                compareToDate={compareToDate}
                domainsToText={domainsToText}
            />
        );
    }
    return (
        <PopTooltipSingleCombined
            {...props}
            date={date}
            compareToDate={compareToDate}
        />
    );
};
